import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UrgencyBanner from "../components/UrgencyBanner";
import { useCart } from "../lib/cart";
import { createCheckout, fetchShippingRates } from "../lib/api";
import { trackBeginCheckout } from "../lib/analytics";
import useSeo from "../lib/useSeo";
import {
  Trash2,
  Minus,
  Plus,
  Ticket,
  ShoppingBag,
  ArrowLeft,
  Loader2,
  Check,
  Sparkles,
  Truck,
  MapPin,
  Mail,
  ShieldCheck,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";

const PHYSICAL_PRODUCT_IDS = new Set([
  "starter_kit", "premium_kit", "addon_buddy", "buddy_print", "pilot_kit",
]);

const DIGITAL_PRODUCT_IDS = new Set([
  "buddy_print_digital", "buddy_coloring_page",
]);

// US states for the address dropdown
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

export default function Cart() {
  const { items, coupon, setCoupon, updateQty, removeItem, preview, recoveryToken, recoverySessionId } = useCart();
  const [couponDraft, setCouponDraft] = useState(coupon || "");
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [shippingRate, setShippingRate] = useState(null);
  const [address, setAddress] = useState({
    full_name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    phone: "",
  });
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();

  useSeo({
    title: "Your cart | Plate Buddies",
    description: "Review your Plate Buddies order and check out securely.",
    url: "https://platebuddies.com/cart",
    noIndex: true,
  });

  // Fire a toast when a coupon is auto-applied via URL param (either at /cart
  // itself, or previously via the global CartProvider on Shop/Home). We only
  // toast once per pageload and only if the URL contains a coupon param —
  // otherwise a manually-typed coupon would spam the toast.
  useEffect(() => {
    const fromUrl = (urlParams.get("coupon") || urlParams.get("promo") || "").trim().toUpperCase();
    if (fromUrl && coupon && coupon.toUpperCase() === fromUrl) {
      toast.success(`Coupon ${fromUrl} applied 🎉`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsShipping = useMemo(
    () => items.some((i) => PHYSICAL_PRODUCT_IDS.has(i.product_id)),
    [items]
  );
  const hasDigital = useMemo(
    () => items.some((i) => DIGITAL_PRODUCT_IDS.has(i.product_id)),
    [items]
  );
  const needsEmail = needsShipping || hasDigital;

  useEffect(() => {
    setCouponDraft(coupon || "");
  }, [coupon]);

  // Load rates whenever the subtotal changes.
  useEffect(() => {
    if (!needsShipping) {
      setRates([]);
      setShippingRate(null);
      return;
    }
    let cancelled = false;
    fetchShippingRates(preview?.subtotal || 0)
      .then((data) => {
        if (cancelled) return;
        const list = data?.rates || [];
        setRates(list);
        // Default selection: first eligible rate (free first if eligible, else standard).
        setShippingRate((prev) => {
          if (prev) {
            const stillEligible = list.find((r) => r.key === prev && r.eligible);
            if (stillEligible) return prev;
          }
          const fav = list.find((r) => r.key === "free" && r.eligible) || list.find((r) => r.key === "standard" && r.eligible);
          return fav ? fav.key : null;
        });
      })
      .catch((err) => console.warn("[fetch] failed:", err));
    return () => { cancelled = true; };
  }, [preview?.subtotal, needsShipping]);

  const selectedRate = rates.find((r) => r.key === shippingRate);
  const shippingAmount = selectedRate?.amount ?? 0;
  const grandTotal = (Number(preview?.total) || 0) + (needsShipping ? Number(shippingAmount) : 0);

  const applyCoupon = (e) => {
    e.preventDefault();
    setCoupon(couponDraft.trim().toUpperCase());
  };

  const clearCoupon = () => {
    setCoupon("");
    setCouponDraft("");
  };

  const validateShipping = () => {
    if (needsEmail && !email.includes("@")) {
      toast.error(hasDigital && !needsShipping
        ? "Enter the email to receive your PDFs."
        : "Enter the email for your order confirmation.");
      return false;
    }
    if (!needsShipping) return true;
    if (!shippingRate) {
      toast.error("Pick a shipping option.");
      return false;
    }
    const required = ["full_name", "line1", "city", "state", "postal_code"];
    for (const k of required) {
      if (!address[k] || !String(address[k]).trim()) {
        toast.error(`Please enter your ${k.replace("_", " ")}.`);
        return false;
      }
    }
    return true;
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (!validateShipping()) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity || 1,
          custom_buddy_id: i.custom_buddy_id,
          custom_buddy_name: i.custom_buddy_name,
          custom_buddy_image: i.custom_buddy_image,
        })),
        coupon: coupon || undefined,
        origin_url: window.location.origin,
        email: email || undefined,
        shipping_rate: needsShipping ? shippingRate : undefined,
        shipping_address: needsShipping ? address : undefined,
        // Campaign gate — only sent when the shopper arrived via a recovery
        // or upsell email link. Server verifies HMAC before honoring the
        // private coupon.
        recovery_token: recoveryToken || undefined,
        recovery_session_id: recoverySessionId || undefined,
      };
      const data = await createCheckout(payload);
      if (data?.url) {
        // GA4 begin_checkout — fired the moment Stripe URL is issued so
        // returning users can be re-targeted in Google Ads even if they
        // bounce off Stripe's page.
        trackBeginCheckout({
          items,
          total: data.total ?? preview?.total,
          coupon: data.coupon?.code || coupon || undefined,
        });
        window.location.href = data.url;
      } else {
        throw new Error("Missing checkout URL");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout. Try again.");
      setLoading(false);
    }
  };

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div data-testid="page-cart" className="font-body bg-pb-cream min-h-screen">
      <UrgencyBanner />
      <Navbar />

      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-20">
        <Link
          to="/home"
          data-testid="link-back-shop"
          className="inline-flex items-center gap-2 text-sm font-bold text-pb-soft hover:text-pb-coral transition"
        >
          <ArrowLeft className="h-4 w-4" /> Keep shopping
        </Link>

        <h1 className="font-display font-black text-5xl md:text-6xl mt-5 leading-[0.95]">
          Your <span className="wavy">cart</span>.
        </h1>
        <p className="text-pb-soft mt-2">{items.length === 0 ? "Empty for now." : `${items.length} item${items.length === 1 ? "" : "s"}`}</p>

        {items.length === 0 ? (
          <div className="mt-10 rounded-[2rem] border-2 border-dashed border-pb-sky/60 p-12 text-center bg-white">
            <ShoppingBag className="h-10 w-10 mx-auto text-pb-coral" />
            <p className="font-display font-black text-2xl mt-4">Nothing in the cart yet</p>
            <p className="text-pb-soft mt-2">Pick a kit, an add-on Buddy, or order a print of your own creation.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/shop" className="rounded-full bg-pb-coral text-white font-extrabold px-6 py-3 hover:scale-105 transition shadow">
                Shop kits
              </Link>
              <Link to="/create" className="rounded-full bg-white text-pb-ink font-bold px-6 py-3 border-2 border-pb-sky/40 hover:border-pb-coral transition">
                Make a Buddy
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-3 gap-8 pb-24 lg:pb-0">
            <div className="lg:col-span-2 space-y-4 min-w-0">
              {items.map((it, idx) => (
                <div
                  key={`${it.product_id}-${it.custom_buddy_id || idx}`}
                  data-testid={`cart-item-${idx}`}
                  className="bg-white rounded-3xl p-4 sm:p-5 shadow-[0_14px_32px_rgba(28,22,40,0.06)] flex gap-3 sm:gap-4 items-start sm:items-center overflow-hidden"
                >
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl overflow-hidden flex-shrink-0 bg-pb-cream border-2 border-pb-sky/30 flex items-center justify-center">
                    {it.custom_buddy_image ? (
                      <img src={it.custom_buddy_image} alt={it.custom_buddy_name} className="w-full h-full object-cover" />
                    ) : (
                      <Sparkles className="h-6 w-6 sm:h-7 sm:w-7 text-pb-coral" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* On mobile the price sits inline with the title (right-aligned) so
                        it never gets pushed off-screen by a long product name. */}
                    <div className="flex items-start gap-2 justify-between">
                      <p className="font-display font-black text-base sm:text-lg leading-tight break-words min-w-0 flex-1">
                        {it.name || it.product_id}
                      </p>
                      <p className="font-display font-black text-base sm:hidden flex-shrink-0">
                        {fmt(it.unit_price * (it.quantity || 1))}
                      </p>
                    </div>
                    {it.custom_buddy_name && (
                      <p className="text-xs font-bold text-pb-coral mt-0.5 truncate">Featuring {it.custom_buddy_name}</p>
                    )}
                    <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                      <div className="inline-flex items-center gap-2 bg-pb-cream rounded-full p-1">
                        <button
                          data-testid={`qty-dec-${idx}`}
                          onClick={() => updateQty(idx, -1)}
                          className="h-8 w-8 rounded-full bg-white hover:bg-pb-sun transition flex items-center justify-center border border-pb-sky/30 touch-manipulation"
                          aria-label="Decrease"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="font-extrabold w-6 text-center text-sm" data-testid={`qty-${idx}`}>{it.quantity || 1}</span>
                        <button
                          data-testid={`qty-inc-${idx}`}
                          onClick={() => updateQty(idx, 1)}
                          className="h-8 w-8 rounded-full bg-white hover:bg-pb-sun transition flex items-center justify-center border border-pb-sky/30 touch-manipulation"
                          aria-label="Increase"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button
                        data-testid={`btn-remove-${idx}`}
                        onClick={() => removeItem(idx)}
                        className="text-xs text-pb-soft hover:text-pb-coral font-bold inline-flex items-center gap-1 touch-manipulation py-1 px-2 -mx-2"
                        aria-label="Remove item"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Remove</span>
                      </button>
                    </div>
                  </div>
                  {/* Desktop-only price column — mobile inlines it with the title. */}
                  <div className="hidden sm:block text-right flex-shrink-0">
                    <p className="font-display font-black text-xl">{fmt(it.unit_price * (it.quantity || 1))}</p>
                  </div>
                </div>
              ))}

              {/* DIGITAL-ONLY EMAIL BLOCK (no shipping) */}
              {hasDigital && !needsShipping && (
                <div
                  data-testid="digital-email-block"
                  className="bg-white rounded-3xl p-6 shadow-[0_14px_32px_rgba(28,22,40,0.06)] space-y-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-pb-coral text-white flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
                        Where should we send your PDFs?
                      </p>
                      <p className="text-sm text-pb-soft mt-1">
                        Digital items are delivered instantly on the next screen — and emailed to this address so you can re-download anytime.
                      </p>
                    </div>
                  </div>
                  <Input
                    testId="digital-email"
                    label="Email"
                    full
                    value={email}
                    onChange={(v) => setEmail(v)}
                    placeholder="you@email.com"
                    type="email"
                  />
                </div>
              )}

              {/* SHIPPING / ADDRESS BLOCK */}
              {needsShipping && (
                <div data-testid="shipping-block" className="bg-white rounded-3xl p-6 shadow-[0_14px_32px_rgba(28,22,40,0.06)] space-y-5">
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral flex items-center gap-1.5">
                      <Truck className="h-3.5 w-3.5" /> Shipping option
                    </p>
                    <div className="mt-3 space-y-2">
                      {rates.map((r) => {
                        const disabled = !r.eligible;
                        const selected = shippingRate === r.key;
                        return (
                          <button
                            key={r.key}
                            type="button"
                            data-testid={`shipping-rate-${r.key}`}
                            disabled={disabled}
                            onClick={() => setShippingRate(r.key)}
                            className={`w-full text-left rounded-2xl p-4 border-2 transition flex items-center gap-4 ${
                              selected
                                ? "border-pb-coral bg-pb-coral/5 shadow-sm"
                                : disabled
                                  ? "border-pb-sky/30 opacity-50 cursor-not-allowed"
                                  : "border-pb-sky/40 hover:border-pb-coral/60 bg-pb-cream"
                            }`}
                          >
                            <span
                              className={`h-5 w-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                selected ? "border-pb-coral" : "border-pb-sky/60"
                              }`}
                            >
                              {selected && <span className="h-2.5 w-2.5 rounded-full bg-pb-coral" />}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-extrabold text-pb-ink text-sm">{r.label}</p>
                              <p className="text-xs text-pb-soft mt-0.5">
                                {r.estimate}
                                {!r.eligible && r.eligibility_note && ` · ${r.eligibility_note}`}
                              </p>
                            </div>
                            <p className="font-display font-black text-lg whitespace-nowrap">
                              {r.amount === 0 ? "FREE" : fmt(r.amount)}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> Ship to
                    </p>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      <Input testId="ship-email" label="Email (order confirmation)" full value={email} onChange={(v) => setEmail(v)} placeholder="you@email.com" type="email" />
                      <Input testId="ship-name" label="Full name" full value={address.full_name} onChange={(v) => setAddress((a) => ({ ...a, full_name: v }))} />
                      <Input testId="ship-line1" label="Address" full value={address.line1} onChange={(v) => setAddress((a) => ({ ...a, line1: v }))} placeholder="123 Main St" />
                      <Input testId="ship-line2" label="Apt / Suite (optional)" full value={address.line2} onChange={(v) => setAddress((a) => ({ ...a, line2: v }))} />
                      <Input testId="ship-city" label="City" value={address.city} onChange={(v) => setAddress((a) => ({ ...a, city: v }))} />
                      <div>
                        <label className="block text-xs font-extrabold uppercase tracking-widest text-pb-soft mb-1.5">State</label>
                        <select
                          data-testid="ship-state"
                          value={address.state}
                          onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))}
                          className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-4 py-2.5 font-bold text-pb-ink focus:outline-none focus:border-pb-coral focus:bg-white transition text-sm"
                        >
                          <option value="">Select…</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <Input testId="ship-zip" label="ZIP" value={address.postal_code} onChange={(v) => setAddress((a) => ({ ...a, postal_code: v }))} />
                      <Input testId="ship-phone" label="Phone (optional)" value={address.phone} onChange={(v) => setAddress((a) => ({ ...a, phone: v }))} type="tel" />
                    </div>
                    <p className="text-[11px] text-pb-soft mt-3">Currently ship to the US only. We'll send a tracking link the moment it leaves the studio.</p>
                  </div>
                </div>
              )}
            </div>

            <aside>
              <div className="bg-white rounded-3xl p-6 shadow-[0_18px_40px_rgba(28,22,40,0.08)] sticky top-24">
                <h3 className="font-display font-black text-2xl">Order summary</h3>
                <div className="mt-5 space-y-2 text-sm">
                  <Row label="Subtotal" value={fmt(preview?.subtotal)} />
                  {preview?.discount > 0 && (
                    <Row
                      label={`Discount (${preview.coupon?.code || ""})`}
                      value={`- ${fmt(preview.discount)}`}
                      accent
                    />
                  )}
                  {needsShipping ? (
                    <Row
                      label="Shipping"
                      value={selectedRate ? (selectedRate.amount === 0 ? "FREE" : fmt(selectedRate.amount)) : "Pick an option"}
                      muted={!selectedRate}
                    />
                  ) : (
                    <Row label="Shipping" value="Digital — no shipping" muted />
                  )}
                </div>

                <div className="mt-5 pt-5 border-t-2 border-dashed border-pb-sky/40 flex items-baseline justify-between">
                  <span className="font-extrabold text-pb-ink">Total</span>
                  <span data-testid="cart-total" className="font-display font-black text-3xl">{fmt(grandTotal)}</span>
                </div>

                {/* Coupon */}
                <form onSubmit={applyCoupon} className="mt-6">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">Coupon code</p>
                  {preview?.coupon?.code ? (
                    <div className="mt-2 flex items-center gap-2 bg-pb-green/10 border-2 border-pb-green/40 rounded-2xl p-3">
                      <Check className="h-4 w-4 text-pb-green flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-extrabold text-sm">{preview.coupon.code}</p>
                        <p className="text-xs text-pb-soft truncate">{preview.coupon.label}</p>
                      </div>
                      <button
                        type="button"
                        onClick={clearCoupon}
                        className="text-xs text-pb-soft hover:text-pb-coral font-bold"
                        data-testid="btn-clear-coupon"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <div className="flex-1 relative">
                        <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-pb-coral" />
                        <input
                          data-testid="input-coupon"
                          value={couponDraft}
                          onChange={(e) => setCouponDraft(e.target.value.toUpperCase())}
                          placeholder="Try WELCOME10"
                          className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream pl-9 pr-3 py-3 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition uppercase"
                        />
                      </div>
                      <button
                        type="submit"
                        data-testid="btn-apply-coupon"
                        className="rounded-2xl bg-pb-ink text-white font-extrabold px-4 hover:bg-pb-coral transition"
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </form>

                <button
                  data-testid="btn-checkout"
                  onClick={checkout}
                  disabled={loading}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-4 hover:scale-[1.02] transition shadow-[0_14px_32px_rgba(242,106,52,0.35)] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
                  Secure checkout
                </button>

                {/* Trust signals — surfaced right before the Stripe hand-off so
                 * cart abandoners get one last reassurance about safety, cost,
                 * and comeback options. Keeps them warm. */}
                <ul
                  data-testid="checkout-trust-list"
                  className="mt-4 space-y-1.5 text-[11px] text-pb-soft"
                >
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5 text-pb-aqua flex-shrink-0" />
                    <span>Secure Stripe checkout · Cards, Apple Pay, Google Pay</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <RefreshCcw className="h-3.5 w-3.5 text-pb-coral flex-shrink-0" />
                    <span>30-day happiness guarantee — reply for a refund, no fuss</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Truck className="h-3.5 w-3.5 text-pb-ink flex-shrink-0" />
                    <span>Free US shipping over $50 · digital items delivered instantly</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-pb-sun flex-shrink-0" />
                    <span>Built by a mom of three · shipped from a real family</span>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        )}
      </section>

      {/* Sticky mobile checkout bar — always visible on <lg viewports so
       * shoppers never have to scroll to find the checkout button, even
       * with a long shipping/address form open.  The bottom padding on the
       * cart section (`pb-24 lg:pb-0`) reserves the space so trailing
       * content isn't hidden underneath. */}
      {items.length > 0 && (
        <div
          data-testid="sticky-mobile-checkout"
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t-2 border-pb-sky/30 shadow-[0_-8px_28px_rgba(28,22,40,0.10)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-soft leading-none">Order total</p>
              <p className="font-display font-black text-xl text-pb-ink leading-tight">
                {fmt(preview?.total ?? preview?.subtotal ?? items.reduce((s,i)=>s+i.unit_price*(i.quantity||1),0))}
              </p>
            </div>
            <button
              data-testid="btn-checkout-mobile"
              onClick={checkout}
              disabled={loading}
              className="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-3.5 shadow-[0_10px_24px_rgba(242,106,52,0.35)] disabled:opacity-60 touch-manipulation"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
              Checkout
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function Row({ label, value, accent, muted }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? "text-pb-soft" : "text-pb-ink"}>{label}</span>
      <span className={`font-extrabold ${accent ? "text-pb-green" : "text-pb-ink"} ${muted ? "text-pb-soft font-bold" : ""}`}>{value}</span>
    </div>
  );
}

function Input({ label, value, onChange, full, testId, placeholder, type = "text" }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-extrabold uppercase tracking-widest text-pb-soft mb-1.5">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-4 py-2.5 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition text-sm"
      />
    </div>
  );
}
